import { useId, useState, type FormEvent } from 'react';
import {
  BUTTON_CLASS,
  DetailDrawer,
  fieldStyle,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import { setSpaceWindow } from '@/lib/api';
import { ACTIVE_WINDOW_DEFAULT_DAYS } from '@/lib/expiry';

/**
 * Shorten (or reset) how long files are kept in a room (RET-12).
 *
 * **`RenameSpacePanel`'s shape, and reached the same way** — the row's "⋯",
 * owner-only, one field and one request. It differs in what it reports back:
 * shortening a window can bring files due at the next nightly sweep, so the
 * response carries `filesNowDue` and this panel shows it rather than closing
 * silently. Nothing is deleted here; the sweep does that, and a mistaken shorten
 * is undone by setting the window back before the next run.
 *
 * **The refusal is the API's own.** A fellow member's admin gets a 403 in the
 * API's wording (`errors.failed` is the fallback for a message-less failure),
 * and a value outside 1–180 is refused there too — the input guards it first so
 * the common case never round-trips.
 */

export interface ExpiryWindowPanelProps {
  open: boolean;
  onClose: () => void;
  /** The room whose window is being set. Addresses the request. */
  spaceId: string;
  /** Its name — the drawer title, for recognition. */
  spaceName: string;
  /** The room's current window in days, already resolved (absent → 180). */
  currentWindowDays: number;
  /**
   * Called once the window has changed, so the caller refetches (§4). This does
   * not close the drawer — the result, including how many files the change
   * brings due, stays on screen until the owner dismisses it.
   */
  onChanged: () => void;
}

interface Result {
  expiryWindowDays: number;
  filesNowDue: number;
}

export function ExpiryWindowPanel({
  open,
  onClose,
  spaceId,
  spaceName,
  currentWindowDays,
  onChanged,
}: ExpiryWindowPanelProps) {
  const daysId = useId();
  const hintId = useId();
  const copy = SPACE_LIST_COPY.expiryWindow;

  const [days, setDays] = useState(String(currentWindowDays));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<Result | undefined>();

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;

    const wanted = Number(days);
    if (!Number.isInteger(wanted) || wanted < 1 || wanted > ACTIVE_WINDOW_DEFAULT_DAYS) {
      setError(copy.errors.range);
      return;
    }

    void send(wanted);
  }

  async function send(wanted: number): Promise<void> {
    setPending(true);
    setError(undefined);
    setResult(undefined);

    try {
      const set = await setSpaceWindow(spaceId, wanted);
      setResult({ expiryWindowDays: set.expiryWindowDays, filesNowDue: set.filesNowDue });
      onChanged();
    } catch (caught) {
      setError(messageFrom(caught, copy.errors.failed));
    } finally {
      setPending(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={copy.title(spaceName)}
      closeLabel={copy.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {copy.intro}
        </p>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={daysId} className="text-xs font-medium">
            {copy.label}
          </label>
          <input
            id={daysId}
            name="expiryWindowDays"
            type="number"
            inputMode="numeric"
            /*
              The 1–180 bound is enforced in `submit` and by the API, not by
              native `min`/`max`: a constraint-invalid number input makes the
              browser silently block submission, so the caller would never see
              this panel's own message (or the API's 403). One authoritative
              check, in JS, is the rule.
            */
            value={days}
            aria-describedby={hintId}
            onChange={(event) => {
              setDays(event.target.value);
            }}
            className="border px-3 py-2 text-sm"
            style={fieldStyle()}
          />
          <span id={hintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {copy.hint}
          </span>
        </div>

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        {result === undefined ? null : (
          <p className="flex items-start gap-2 text-sm" style={{ color: token('bp-text-muted') }}>
            <StatusDot tone={result.filesNowDue > 0 ? 'amber' : 'success'} />
            {result.filesNowDue > 0
              ? copy.result.due(result.expiryWindowDays, result.filesNowDue)
              : copy.result.set(result.expiryWindowDays)}
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className={BUTTON_CLASS}
            style={primaryButtonStyle()}
          >
            {pending ? copy.submitting : copy.submit}
          </button>

          <button
            type="button"
            onClick={onClose}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {result === undefined ? copy.cancel : copy.done}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
